import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowLeft, Search, Filter, Download } from "lucide-react";

const AREA_LABEL = {
  caja: "Caja", cocina: "Cocina", gerente_sucursal: "Gerente Sucursal",
  operaciones: "Operaciones", rh: "RH y Nominas", compras: "Compras",
  direccion: "Direccion", legal: "Legal", tesoreria: "Tesoreria",
  diseno: "Diseno", otro: "Otro",
  rh_nomina: "RRHH y Nómina", gerente_operaciones: "Gerente de Operaciones",
};
const AREA_COLOR = {
  caja: "bg-blue-50 text-blue-700", cocina: "bg-orange-50 text-orange-700",
  gerente_sucursal: "bg-purple-50 text-purple-700", operaciones: "bg-teal-50 text-teal-700",
  rh: "bg-pink-50 text-pink-700", compras: "bg-emerald-50 text-emerald-700",
  direccion: "bg-slate-100 text-slate-700", legal: "bg-amber-50 text-amber-700",
  tesoreria: "bg-blue-50 text-blue-700", diseno: "bg-indigo-50 text-indigo-700",
  otro: "bg-slate-50 text-slate-500",
  rh_nomina: "bg-pink-50 text-pink-700", gerente_operaciones: "bg-teal-50 text-teal-700",
};
const STATUS_COLOR = {
  verde: "bg-green-100 text-green-800",
  amarillo: "bg-yellow-100 text-yellow-800",
  rojo: "bg-red-100 text-red-800",
};
const ALL_AREAS = ["caja", "cocina", "gerente_sucursal", "gerente_operaciones", "rh_nomina", "compras", "direccion", "legal", "tesoreria", "diseno"];

function fmtDate(period) {
  if (!period) return "";
  const [y, m, d] = period.split("-");
  return `${d}/${m}/${y}`;
}

function exportCsv(records) {
  const headers = ["Fecha", "KPI", "Area", "Sucursal", "Valor", "Meta", "Cumplimiento %", "Estado", "Notas", "Cargado por"];
  const rows = records.map(r => [
    r.period, r.kpi_name, AREA_LABEL[r.area] || r.area,
    r.branch_name || "Corporativo", r.value, r.target, r.achievement_pct,
    r.status, r.notes || "", r.created_by_name || "",
  ]);
  const csv = [headers, ...rows].map(row => row.map(c => `"${c}"`).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = "kpis_historico.csv";
  a.click(); URL.revokeObjectURL(url);
}

// ── Drawer de detalle ────────────────────────────────────────
function DetailDrawer({ record, onClose }) {
  if (!record) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-sm bg-white h-full overflow-y-auto p-6 space-y-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">{record.kpi_name}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400">x</button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-500">Área</span><div className="font-medium mt-0.5">{AREA_LABEL[record.area] || record.area}</div></div>
          <div><span className="text-slate-500">Período</span><div className="font-medium mt-0.5">{fmtDate(record.period)}</div></div>
          <div><span className="text-slate-500">Valor</span><div className="font-medium mt-0.5">{record.value} {record.unit}</div></div>
          <div><span className="text-slate-500">Meta</span><div className="font-medium mt-0.5">{record.target} {record.unit}</div></div>
          <div><span className="text-slate-500">Cumplimiento</span><div className="font-medium mt-0.5">{record.achievement_pct}%</div></div>
          <div><span className="text-slate-500">Estado</span>
            <div className={`inline-block mt-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[record.status] || ""}`}>{record.status}</div>
          </div>
          <div className="col-span-2"><span className="text-slate-500">Sucursal</span><div className="font-medium mt-0.5">{record.branch_name || "Corporativo"}</div></div>
          <div className="col-span-2"><span className="text-slate-500">Cargado por</span><div className="font-medium mt-0.5">{record.created_by_name || "—"}</div></div>
          {record.notes && (
            <div className="col-span-2">
              <span className="text-slate-500">Notas</span>
              <div className="mt-0.5 text-sm text-slate-700 bg-slate-50 rounded-lg p-2">{record.notes}</div>
            </div>
          )}
          {record.updated_at && (
            <div className="col-span-2">
              <span className="text-xs text-slate-400">Editado el {fmtDate(record.updated_at?.slice(0, 10))}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OpsKpisHistorico() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCorp = user?.role === "corporate_admin";

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [kpiDefs, setKpiDefs] = useState([]);
  const [selected, setSelected] = useState(null);

  // Filtros
  const [filterArea, setFilterArea] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterArea) params.set("area", filterArea);
      if (filterBranch) params.set("branch_id", filterBranch);
      if (filterFrom) params.set("from_date", filterFrom);
      if (filterTo) params.set("to_date", filterTo);
      const res = await api.get(`/ops/kpis/history?${params}`);
      setRecords(res.data || []);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filterArea, filterBranch, filterFrom, filterTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isCorp) {
      api.get("/branches").then(r => setBranches(r.data || [])).catch(() => {});
    }
    api.get("/ops/kpis/definitions?active_only=false").then(r => setKpiDefs(r.data || [])).catch(() => {});
  }, [isCorp]);

  const legacyIds = new Set(kpiDefs.filter(k => k.legacy).map(k => k.id));

  const filtered = records.filter(r =>
    !search || r.kpi_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/ops/kpis")}
          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
          data-testid="kpi-historico-back-btn"
        >
          <ArrowLeft className="w-4 h-4 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Historico de KPIs</h1>
          <p className="text-sm text-slate-500">Registros cargados por periodo</p>
        </div>
        <button
          onClick={() => exportCsv(filtered)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          data-testid="kpi-export-btn"
        >
          <Download className="w-4 h-4" /> Exportar
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="relative col-span-2 sm:col-span-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar KPI..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            data-testid="kpi-search-input"
          />
        </div>
        <select
          value={filterArea}
          onChange={e => setFilterArea(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
          data-testid="kpi-filter-area"
        >
          <option value="">Todas las areas</option>
          {ALL_AREAS.map(a => <option key={a} value={a}>{AREA_LABEL[a]}</option>)}
        </select>
        {isCorp && (
          <select
            value={filterBranch}
            onChange={e => setFilterBranch(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            data-testid="kpi-filter-branch"
          >
            <option value="">Todas las sucursales</option>
            {branches.filter(b => !b.is_corporate).map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
          data-testid="kpi-filter-from"
        />
        <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
          data-testid="kpi-filter-to"
        />
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => <div key={i} className="bg-white h-12 rounded-lg border border-slate-100 animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="kpi-historico-empty">
          Sin registros para los filtros seleccionados.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kpi-history-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Fecha</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">KPI</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Area</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Sucursal</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Valor</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Cumpl %</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Estado</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wide">Responsable</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={r.id || i}
                    onClick={() => setSelected(r)}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`kpi-history-row-${i}`}
                  >
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{fmtDate(r.period)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 max-w-[180px] truncate">
                      {r.kpi_name}
                      {legacyIds.has(r.kpi_definition_id) && (
                        <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500" data-testid={`kpi-history-archived-badge-${i}`}>Archivado</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${AREA_COLOR[r.area] || "bg-slate-50 text-slate-500"}`}>
                        {AREA_LABEL[r.area] || r.area}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{r.branch_name || "Corporativo"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{r.value}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.achievement_pct}%</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLOR[r.status] || "bg-slate-100 text-slate-600"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[120px] truncate">{r.created_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400">
            {filtered.length} registros
          </div>
        </div>
      )}

      <DetailDrawer record={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
